import SkeletonBase from "newui/components/Skeleton";
import { getCompletionStatus } from "newui/features/Rfx/Evaluation/api/scoring-api";
import { saveOrSubmitScores } from "newui/features/Rfx/Evaluation/api/panel-scoring-api";
import { useInitialEvaluationContext } from "newui/features/Rfx/Evaluation/hooks/InitialEvaluationContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn, showToast } from "utils/helpers";
import { EvaluationClarificationsTable } from "../panel";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { SupplierResponseTable } from "../SupplierResponseTable";
import { ScoringModal } from "../ScoringModal";
import { SubmitConfirmation } from "../panel/member/SubmitConfirmation";
import { SubmitEvaluationConfirmDialog } from "../../../SubmitEvaluationConfirmDialog";
import { initialEvaluationSteps } from "guides/features/Evaluation";

const SOLO_SCORING_INSTRUCTIONS: React.ReactNode[] = [
	"Score each supplier against the evaluation criteria. Click a supplier row to open the scoring modal.",
	"Use the scoring guide for reference on how to assign scores. Add comments to explain your rationale.",
	"Your scores are saved automatically as you work. All suppliers must be scored before you can proceed.",
	<>Once submitted, initial scores are <strong>final</strong> and cannot be modified.</>,
];

type SoloTabId = "my-scoring" | "my-clarifications";

interface SoloEvaluationViewProps {
	evalId?: number;
	/**
	 * True when the viewer IS the RFx Lead — solo mode is usually the Lead evaluating
	 * alone, but a non-lead company user can also land here, and the clarifications
	 * info message differs for the two.
	 */
	isRfxLead?: boolean;
	isLoading: boolean;
	rfxId: number;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
}

export const SoloEvaluationView: React.FC<SoloEvaluationViewProps> = ({
	rfxId,
	evalId,
	isLoading,
	isRfxLead = false,
	onBeforeNext,
	onCompletionChange,
}) => {
	const [activeTab, setActiveTab] = useState<SoloTabId>("my-scoring");
	const [selectedInviteId, setSelectedInviteId] = useState<number | null>(null);
	const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
	const [confirmChecked, setConfirmChecked] = useState(false);
	const pendingResolveRef = useRef<((value: boolean) => void) | null>(null);

	const {
		suppliers,
		isLoading: isSuppliersLoading,
		error,
		allSuppliersScored,
		readOnly,
		submittedAt,
		notPermitted,
		fetchSuppliers,
		refreshSupplier,
	} = useInitialEvaluationContext();

	// Re-fetch suppliers when this step mounts — the initial provider-level
	// fetch may have run before the backend step was advanced to >= 2.
	useEffect(() => {
		fetchSuppliers();
	}, [fetchSuppliers]);

	// Report completion state to parent for enabling/disabling the Next button
	useEffect(() => {
		onCompletionChange?.(allSuppliersScored);
	}, [allSuppliersScored, onCompletionChange]);

	const handleSelectSupplier = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleCloseModal = useCallback(() => {
		if (selectedInviteId !== null) {
			refreshSupplier(selectedInviteId);
		}
		setSelectedInviteId(null);
	}, [selectedInviteId, refreshSupplier]);

	const handleSupplierChange = useCallback((inviteId: number) => {
		setSelectedInviteId(inviteId);
	}, []);

	const handleScoringComplete = useCallback(() => {
		if (selectedInviteId !== null) {
			refreshSupplier(selectedInviteId);
		}
	}, [selectedInviteId, refreshSupplier]);

	const handleConfirmSubmit = useCallback(async () => {
		setShowSubmitConfirm(false);
		setConfirmChecked(false);
		if (!pendingResolveRef.current) return;

		if (evalId) {
			try {
				await saveOrSubmitScores(rfxId, evalId, { action: "submit", scores: [] });
			} catch {
				showToast("Failed to submit scores. Please try again.", "Error", false);
				pendingResolveRef.current(false);
				pendingResolveRef.current = null;
				return;
			}
		}

		pendingResolveRef.current(true);
		pendingResolveRef.current = null;
	}, [rfxId, evalId]);

	const handleCancelSubmit = useCallback(() => {
		setShowSubmitConfirm(false);
		setConfirmChecked(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
	}, []);

	const checkBeforeNext = useCallback(async (): Promise<boolean> => {
		try {
			const completion = await getCompletionStatus(rfxId);
			if (!completion.isComplete) {
				const { summary } = completion;
				showToast(
					`All suppliers must be fully scored before submitting. ${summary.fullyScored}/${summary.totalSuppliers} suppliers scored.`,
					"Evaluation Incomplete",
					false
				);
				return false;
			}
		} catch {
			showToast("Failed to check completion status. Please try again.", "Error", false);
			return false;
		}

		// Show confirmation dialog and wait for user response
		return new Promise<boolean>((resolve) => {
			pendingResolveRef.current = resolve;
			setShowSubmitConfirm(true);
		});
	}, [rfxId]);

	useEffect(() => {
		onBeforeNext?.(checkBeforeNext);
	}, [onBeforeNext, checkBeforeNext]);

	if (isLoading || isSuppliersLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	if (notPermitted) {
		return (
			<div data-testid="initial-evaluation-not-permitted" className="space-y-4">
				<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
					<span className="mt-0.5 text-amber-600" aria-hidden="true">&#9888;</span>
					<div>
						<p className="text-sm font-medium text-amber-800">
							You are not permitted to score this evaluation.
						</p>
						<div className="mt-1 text-sm text-amber-700">
							<div className="pt-4">
								Only the RFx Lead can score this evaluation. Current progress is shown below.
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const showTabs = !!evalId;

	return (
		<div data-testid="initial-evaluation-step">
			{readOnly && !submittedAt ? (
				<div className="text-sm text-gray-500 mb-4">
					<div className="pt-4">
						Only the RFx Lead can score this evaluation. Current progress is shown below.
					</div>
				</div>
			) : !submittedAt && (
				<div className="mb-4">
					<InstructionsInfoBox instructions={SOLO_SCORING_INSTRUCTIONS} guideSteps={initialEvaluationSteps} />
				</div>
			)}

			{error && (
				<p className="mb-4 text-sm text-red-600" data-testid="evaluation-error">
					{error}
				</p>
			)}

			{showTabs ? (
				<>
					<div className="flex border-b border-gray-200" data-testid="evaluation-tabs" role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === "my-scoring"}
							aria-controls="tab-panel-my-scoring"
							onClick={() => setActiveTab("my-scoring")}
							className={cn(
								"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
								activeTab === "my-scoring"
									? "border-[#0031DD] text-[#0031DD]"
									: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
							)}
							data-testid="tab-my-scoring"
						>
							{readOnly ? "Supplier " : "My "}Scoring
						</button>
						{!readOnly && <button
							type="button"
							role="tab"
							aria-selected={activeTab === "my-clarifications"}
							aria-controls="tab-panel-my-clarifications"
							onClick={() => setActiveTab("my-clarifications")}
							className={cn(
								"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
								activeTab === "my-clarifications"
									? "border-[#0031DD] text-[#0031DD]"
									: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
							)}
							data-testid="tab-my-clarifications"
						>
							My Clarifications
						</button>}
					</div>

					<div
						id="tab-panel-my-scoring"
						role="tabpanel"
						className={activeTab === "my-scoring" ? "mt-4 space-y-4" : "hidden"}
					>
						{readOnly && submittedAt && evalId ? (
							<SubmitConfirmation rfxId={rfxId} evalId={evalId} submittedAt={submittedAt} description="Your initial scores have been submitted and are now locked. You can review them below." />
						) : (
							<SupplierResponseTable
								suppliers={suppliers}
								isLoading={isSuppliersLoading}
								rfxId={rfxId}
								onSelectSupplier={readOnly ? undefined : handleSelectSupplier}
								readOnly={readOnly}
							/>
						)}
					</div>

					{!readOnly && <div
						id="tab-panel-my-clarifications"
						role="tabpanel"
						className={activeTab === "my-clarifications" ? "mt-4 space-y-4" : "hidden"}
					>
						<EvaluationClarificationsTable
							rfxId={rfxId}
							evalId={evalId!}
							isRfxLead={isRfxLead}
							suppliers={suppliers.map((s) => ({
								inviteId: s.inviteId,
								supplierName: s.supplierName,
								supplierLogo: s.supplierLogo,
							}))}
						/>
					</div>}
				</>
			) : (
				<SupplierResponseTable
					suppliers={suppliers}
					isLoading={isSuppliersLoading}
					rfxId={rfxId}
					onSelectSupplier={handleSelectSupplier}
					readOnly={readOnly}
				/>
			)}

			{selectedInviteId !== null && !readOnly && (
				<ScoringModal
					isOpen={selectedInviteId !== null}
					rfxId={rfxId}
					evalId={evalId}
					suppliers={suppliers}
					selectedInviteId={selectedInviteId}
					onClose={handleCloseModal}
					onSupplierChange={handleSupplierChange}
					onScoringComplete={handleScoringComplete}
				/>
			)}

			{showSubmitConfirm && (
				<SubmitEvaluationConfirmDialog
					isOpen={showSubmitConfirm}
					mode="initial"
					suppliers={suppliers.map((s) => ({
						inviteId: s.inviteId,
						supplierName: s.supplierName,
					}))}
					criteria={[]}
					confirmChecked={confirmChecked}
					onConfirmCheckedChange={setConfirmChecked}
					onConfirm={handleConfirmSubmit}
					onCancel={handleCancelSubmit}
				/>
			)}
		</div>
	);
};